import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

type Glyph = "film" | "cassette" | "antenna" | "shelf" | "search" | "default";

interface Props {
  title: string;
  description?: string;
  action?: ReactNode;
  glyph?: Glyph;
  className?: string;
}

/**
 * The teaching moment of the app. Each empty state pairs:
 *   - a stylized line-art SVG that hints at the domain
 *   - a clear, short title
 *   - one explanatory line
 *   - one action (verb-led button)
 *
 * The art uses currentColor + a brass accent so it plays nicely with
 * theme changes. Lines are intentionally hand-drawn-feeling: round
 * caps, slight asymmetry, no pictograms.
 */
export function EmptyState({
  title,
  description,
  action,
  glyph = "default",
  className,
}: Props) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-5 rounded-lg " +
          "border border-dashed border-line bg-surface/30 px-8 py-14 text-center",
        className,
      )}
    >
      <Art glyph={glyph} />
      <div className="space-y-1.5">
        <p className="font-display text-lg font-medium tracking-display-tight">
          {title}
        </p>
        {description && (
          <p className="mx-auto max-w-sm text-sm leading-relaxed text-foreground/60">
            {description}
          </p>
        )}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

function Art({ glyph }: { glyph: Glyph }) {
  const common = "h-20 w-20 text-foreground/35";
  switch (glyph) {
    case "film":
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          <rect
            x={10}
            y={18}
            width={60}
            height={44}
            rx={3}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          {/* Sprocket holes */}
          {[24, 40, 56].map((x) => (
            <g key={x}>
              <rect
                x={x - 2.5}
                y={22}
                width={5}
                height={4}
                rx={1}
                fill="currentColor"
              />
              <rect
                x={x - 2.5}
                y={54}
                width={5}
                height={4}
                rx={1}
                fill="currentColor"
              />
            </g>
          ))}
          {/* Frame outline */}
          <rect
            x={20}
            y={30}
            width={40}
            height={20}
            rx={1.5}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          {/* Brass accent — the "lit frame" */}
          <path
            d="M28 38h24M28 42h16"
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        </svg>
      );
    case "cassette":
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          <rect
            x={8}
            y={22}
            width={64}
            height={40}
            rx={4}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          <rect
            x={16}
            y={32}
            width={48}
            height={20}
            rx={2}
            stroke="currentColor"
            strokeWidth={1.2}
          />
          <circle
            cx={28}
            cy={42}
            r={5}
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.4}
          />
          <circle cx={28} cy={42} r={1.6} fill="hsl(38 56% 62%)" />
          <circle
            cx={52}
            cy={42}
            r={5}
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.4}
          />
          <circle cx={52} cy={42} r={1.6} fill="hsl(38 56% 62%)" />
          <path
            d="M22 58h36"
            stroke="currentColor"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        </svg>
      );
    case "antenna":
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          <rect
            x={26}
            y={42}
            width={28}
            height={22}
            rx={2}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          <circle
            cx={34}
            cy={54}
            r={3}
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.4}
          />
          <path
            d="M44 50h6M44 56h6"
            stroke="currentColor"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
          <path
            d="M40 42 30 18M40 42l10-24"
            stroke="currentColor"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
          <circle cx={30} cy={18} r={2} fill="hsl(38 56% 62%)" />
          <circle cx={50} cy={18} r={2} fill="hsl(38 56% 62%)" />
          {/* Signal arcs */}
          <path
            d="M22 14a18 18 0 0 1 36 0"
            stroke="hsl(38 56% 62%)"
            strokeWidth={1}
            strokeLinecap="round"
            strokeDasharray="2 3"
            opacity={0.6}
          />
        </svg>
      );
    case "shelf":
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          {[20, 38, 56].map((y) => (
            <line
              key={y}
              x1={10}
              y1={y}
              x2={70}
              y2={y}
              stroke="currentColor"
              strokeWidth={1.4}
              strokeLinecap="round"
            />
          ))}
          {/* A few books on each shelf */}
          {[
            { x: 14, y: 20, h: 14 },
            { x: 22, y: 20, h: 16 },
            { x: 30, y: 20, h: 12 },
            { x: 16, y: 38, h: 14 },
            { x: 24, y: 38, h: 17 },
            { x: 50, y: 38, h: 13 },
            { x: 14, y: 56, h: 12 },
            { x: 56, y: 56, h: 14 },
          ].map((b, i) => (
            <rect
              key={i}
              x={b.x}
              y={b.y - b.h}
              width={5}
              height={b.h}
              rx={0.5}
              stroke="currentColor"
              strokeWidth={1.2}
              fill={i % 4 === 0 ? "hsl(38 56% 62%)" : "transparent"}
              fillOpacity={i % 4 === 0 ? 0.3 : undefined}
            />
          ))}
        </svg>
      );
    case "search":
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          <circle
            cx={34}
            cy={34}
            r={18}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          <path
            d="m48 48 14 14"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
          />
          <path
            d="M28 30c2-3 6-4 10-3"
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 80 80" className={common} fill="none" aria-hidden>
          <rect
            x={16}
            y={18}
            width={48}
            height={44}
            rx={3}
            stroke="currentColor"
            strokeWidth={1.4}
          />
          <path
            d="M28 32h24M28 40h16M28 48h20"
            stroke="hsl(38 56% 62%)"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </svg>
      );
  }
}
